import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  setSearchParams,
  updateSearchField,
  fetchAutocompleteSuggestions,
} from "../store/slices/searchSlice";
import { slugify } from "../utils/utils";

const getQueryString = (q) =>
  typeof q === "object" && q?.name ? q.name : String(q || "");

const CategoryDropdown = ({ onChange }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { categories = [] } = useSelector((state) => state.categories);
  const {
    query: reduxQuery,
    autocompleteSuggestions = [],
    loading,
  } = useSelector((state) => state.search);

  const [inputValue, setInputValue] = useState(() => {
    const initialReduxQuery = getQueryString(reduxQuery);
    const navStateSearchData = location.state?.searchData;
    return initialReduxQuery || (navStateSearchData?.query ? getQueryString(navStateSearchData.query) : "");
  });

  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceTimeoutRef = useRef(null);
  const previousDebouncedInputValueRef = useRef("");
  const parentCategories = useMemo(
    () => categories.filter((cat) => !cat.parent_id),
    [categories]
  );

  useEffect(() => {
    const currentReduxQuery = getQueryString(reduxQuery);
    const navStateSearchData = location.state?.searchData;
    const querySource = navStateSearchData?.query ? getQueryString(navStateSearchData.query) : currentReduxQuery;

    if (querySource !== inputValue) {
      setInputValue(querySource || "");
      setShowSuggestions(false);
    }
  }, [reduxQuery, location.state, inputValue]);


  useEffect(() => {
    const trimmedInputValue = inputValue.trim();

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (trimmedInputValue === "") {
      previousDebouncedInputValueRef.current = "";
      return;
    }


    if (trimmedInputValue !== previousDebouncedInputValueRef.current) {
      debounceTimeoutRef.current = setTimeout(() => {
        dispatch(fetchAutocompleteSuggestions({ query: trimmedInputValue }));
        previousDebouncedInputValueRef.current = trimmedInputValue;
      }, 300);
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [inputValue, dispatch]);


  const itemsToDisplay = useMemo(() => {
    const trimmed = inputValue.trim();
    if (trimmed === "") {
      return parentCategories.map(pc => ({ ...pc, type: 'category' }));
    }
    return autocompleteSuggestions;
  }, [inputValue, parentCategories, autocompleteSuggestions]);


  const normalizedItems = useMemo(() => {
    return Array.isArray(itemsToDisplay)
      ? itemsToDisplay.filter(
        (item) =>
          item &&
          typeof item === "object" &&
          "id" in item &&
          "name" in item
      )
      : [];
  }, [itemsToDisplay]);


  const findTopLevelParentName = useCallback((itemId, allCategories) => {
    if (!itemId || !allCategories || allCategories.length === 0) return "";
    const numItemId = Number(itemId);

    let itemToParentMap = new Map();
    let queue = [...allCategories];


    let targetItemInitial;

    while (queue.length > 0) {
      const current = queue.shift();
      if (current.id === numItemId) targetItemInitial = current;

      if (current.children && current.children.length > 0) {
        for (const child of current.children) {
          itemToParentMap.set(child.id, current);
          queue.push(child);
          if (child.id === numItemId) targetItemInitial = child;
        }
      }
    }

    if (!targetItemInitial) return "";

    let currentAncestor = targetItemInitial;
    while (itemToParentMap.has(currentAncestor.id)) {
      currentAncestor = itemToParentMap.get(currentAncestor.id);
    }
    return currentAncestor.name;
  }, []);


  const handleItemSelect = useCallback(
    (item) => {
      setShowSuggestions(false);

      const itemType = item.type || 'category';

      if (itemType === "etablissement") {
        setInputValue(String(item.name || ""));
        const searchData = {
          query: item.name,
          type: 'etablissement',
          etablissementId: item.id,
          categoryId: null,
          categoryName: "",
        };
        // console.log({item})
        dispatch(setSearchParams(searchData));
        // navigate(`/etablissement/${item.id}`);
         navigate(`/etablissement/${item?.slug}`,{state:{idEtablissement:item?.id}})
        
      } else {
        const topLevelParentName = findTopLevelParentName(item.id, categories);
        const searchData = {
          query: item.name,
          type: 'category',
          categoryId: item.id,
          categoryName: topLevelParentName || item.name,
          etablissementId: null,
        };
        dispatch(setSearchParams(searchData));
        dispatch(updateSearchField({ field: "saisie", value: false }));
        setInputValue(item.name);

        if (onChange) {
          onChange(searchData);
        }
      }
    },
    [dispatch, navigate, onChange, categories, findTopLevelParentName]
  );

  const handleInputChange = useCallback(
    (e) => {
      const currentValue = e.target.value;
      setInputValue(currentValue);
      setShowSuggestions(true);
      dispatch(updateSearchField({ field: "query", value: currentValue }));
      dispatch(updateSearchField({ field: "saisie", value: currentValue.length>0 }));

      if (!currentValue.trim()) {
        dispatch(setSearchParams({
          query: "", categoryId: null, categoryName: "", type: null, etablissementId: null,
        }));
      } else {
        dispatch(setSearchParams({
          categoryId: null, categoryName: "", type: 'query', etablissementId: null,
        }));
      }
    },
    [dispatch]
  );

  const handleClear = () => {
    setInputValue("");
    const resetData = { query: "", categoryId: null, categoryName: "", type: null, etablissementId: null, filtre: [] /* any other fields to reset */ };
    dispatch(setSearchParams(resetData));
    setShowSuggestions(false);
    previousDebouncedInputValueRef.current = "";
    if (onChange) {
      onChange(resetData);
    }
  };

  const handleFocus = () => {
    if (inputValue.trim() || parentCategories.length > 0) {
      setShowSuggestions(true);
      const trimmed = inputValue.trim();
      if (trimmed && trimmed !== previousDebouncedInputValueRef.current) {
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = setTimeout(() => {
          dispatch(fetchAutocompleteSuggestions({ query: trimmed }));
          previousDebouncedInputValueRef.current = trimmed;
        }, 300);
      }
    }
  };


  return (
    <div className="dropdown">
      <input
        type="text"
        className="form-control custom-input search-input"
        placeholder={"Service, spécialité, établissement ..."}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
      />
      {inputValue && (
        <button type="button" className="link-blue absolute-close" onClick={handleClear}>
          <img src="/images/x-03.svg" alt="Clear search" />
        </button>
      )}
      {showSuggestions && (
        <div
          className="dropdown-menu show"
          style={{ maxHeight: "300px", overflowY: "auto", width: "100%" }}
        >
          {loading && <span className="dropdown-item text-muted">Chargement...</span>}

          {!loading && normalizedItems.length > 0 &&
            normalizedItems.map((item) => (
              <button
                key={`${item.type || 'cat'}-${item.id}`}
                className={`dropdown-item d-block w-100 text-start ${item.parent_id ? "pl-4" : (item.type === "category" && !item.parent_id) ? "font-weight-bold" : ""}`}
                onClick={() => handleItemSelect(item)}
                type="button"
              >
                {item.name}
                {item.type === "etablissement" && <span className="badge bg-secondary ms-2">Établissement</span>}
              </button>
            ))}

          {!loading && normalizedItems.length === 0 && inputValue.trim() && (
            <span className="dropdown-item text-muted">Aucun résultat trouvé</span>
          )}

          {!inputValue.trim() && !loading && itemsToDisplay === parentCategories.map(pc => ({ ...pc, type: 'category' })) && itemsToDisplay.length > 0 && (
            <span className="dropdown-item text-muted disabled">Catégories populaires</span>
          )}


        </div>
      )}
    </div>
  );
};

export default CategoryDropdown;